import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;

if (!BASE_URL) {
  // CRA only inlines env vars prefixed with REACT_APP_, anything else
  // (e.g. FUNCTION_URL) is always undefined in the browser bundle.
  console.error(
    'REACT_APP_API_URL is not set. Set it in .env (local) or as a build-time ' +
    'secret in the deploy workflow, otherwise every API call will fail.'
  );
}

export const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

type LambdaAction =
  | 'get_locations'
  | 'get_menu'
  | 'get_news'
  | 'get_categories';

interface LambdaEnvelope {
  statusCode: number;
  body: unknown;
}

function isLambdaEnvelope(value: unknown): value is LambdaEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    'statusCode' in value &&
    'body' in value
  );
}

/**
 * lambda_handler always builds its reply as `{ statusCode, body, headers }`.
 * A Lambda Function URL unwraps that into the real HTTP response, so axios
 * normally gets the parsed `body` straight away. This also tolerates the
 * envelope arriving unwrapped, or `body` arriving as a raw JSON string, so
 * callers never need to worry about the transport shape.
 */
function unwrap<T>(data: unknown): T {
  let payload = data;

  if (isLambdaEnvelope(payload)) {
    if (payload.statusCode < 200 || payload.statusCode >= 300) {
      throw new Error(`API request failed with status ${payload.statusCode}`);
    }
    payload = payload.body;
  }

  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      // already plain text, leave as-is
    }
  }

  return payload as T;
}

export async function callAction<T>(
  action: LambdaAction,
  params: Record<string, string | number | undefined> = {}
): Promise<T> {
  const response = await httpClient.get('', {
    params: { action, ...params },
  });
  return unwrap<T>(response.data);
}
